import { yupResolver } from '@hookform/resolvers/yup';
import CountryListModule from 'country-list-with-dial-code-and-flag';
import { isValidPhoneNumber } from 'libphonenumber-js';
import ls from 'localstorage-slim';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { EDevice } from '../../../../redux/sequence/enums';
import { RootState } from '../../../../redux/types';
import {
  LegalConditionsMarketing,
  PrivateCustomer,
} from '../../../../types/instant-lease-api';
import { getPartnerText } from '../../../../utils/getter';
import Button from '../../../b2b/components/button/button';
import Checkbox from '../../../b2b/components/checkbox/checkbox';
import InternationalPhoneInput from '../../../b2b/components/international-phone-input/international-phone-input';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import FormLayout from '../../../common/form-layout/form-layout';
import InputField from '../../../common/input-field/input-field';

// Handle CJS/ESM interop – the actual instance may be on `.default`
const CountryList = ((CountryListModule as any).default ??
  CountryListModule) as typeof CountryListModule;

const getDialCodeDetails = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return {
      dialCode: '+49',
      dialCodeLength: 0,
    };
  }

  let dialCodeLength = 0;
  for (let i = 0; i <= 5; i++) {
    const matchedCountries = CountryList.findByDialCode(
      phoneNumber.slice(0, i),
    );
    if (matchedCountries.length === 1) {
      dialCodeLength = i;
    }
  }

  return {
    dialCode: phoneNumber.slice(0, dialCodeLength) || '+49',
    dialCodeLength,
  };
};

interface ContactDataProps {
  onSubmit: (owner: ContactTemplateData) => void;
  tabButton: boolean;
  privateCustomer: PrivateCustomer | undefined;
  loading?: boolean;
  marketingAgreements?: LegalConditionsMarketing;
  singer?: boolean;
  hasCoApplicant?: boolean;
}

export interface ContactTemplateData {
  email: string;
  phone_number: string;
  marketing_email?: boolean | undefined;
  marketing_phone?: boolean | undefined;
}

const ContactTemplate: React.FC<ContactDataProps> = ({
  privateCustomer,
  tabButton,
  onSubmit,
  loading,
  marketingAgreements,
  singer,
  hasCoApplicant,
}) => {
  // Auth Storage
  const { email } = useSelector((state: RootState) => state.auth);

  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // check whether is it posbank loyalty prometion
  const isPostBank =
    activeApplication?.loyalty_card?.loyalty_card_type?.toLowerCase() ===
    'postbank';

  // validationSchema
  const validationSchema = yup.object().shape({
    phone_number: yup
      .string()
      .matches(
        /^[1-9][0-9]+$/gm,
        'Bitte geben Sie eine gültige Handynummer ohne führende 0 an, z.B.: 40471040',
      )
      .test(
        'is-valid-phone',
        'Bitte geben Sie eine gültige Handynummer ohne führende 0 an, z.B.: 40471040',
        (value) => {
          if (!value) return false;
          const fullNumber = `${countryCode}${value}`;
          return isValidPhoneNumber(fullNumber);
        },
      )
      .required('Pflichtfeld'),
    email: yup
      .string()
      .email('Bitte vollständige Email Adresse eintragen')
      .required('Pflichtfeld')
      .test(
        'main-signer-email-different',
        'Bitte verwende eine andere E-Mail-Adresse als der Antragsteller.',
        (value) => {
          if (!hasCoApplicant) {
            return true;
          }
          const storedEmail = ls.get('email') as string | null | undefined;
          if (!value || !storedEmail) {
            return true;
          }
          return (
            value.trim().toLowerCase() !== storedEmail.trim().toLowerCase()
          );
        },
      ),
    marketing_email: yup.boolean().optional(),
    marketing_phone: yup.boolean().optional(),
  });

  const contacts = privateCustomer?.person?.contact_details;
  const contactPhoneNumber = contacts?.phone_numbers?.[0]?.value || '';
  const { dialCode: initialDialCode } = getDialCodeDetails(contactPhoneNumber);

  // Set country code when user pick flag
  const [countryCode, setCountry] = useState<string>(initialDialCode);

  // React 19 pattern: adjust state during render when source data changes
  const [prevContactPhoneNumber, setPrevContactPhoneNumber] =
    useState(contactPhoneNumber);
  if (contactPhoneNumber !== prevContactPhoneNumber) {
    setPrevContactPhoneNumber(contactPhoneNumber);
    setCountry(getDialCodeDetails(contactPhoneNumber).dialCode);
  }

  const getEmail = () => {
    if (contacts?.emails && contacts?.emails?.length > 0) {
      return contacts?.emails[0].value;
    } else if (!singer) {
      return email;
    }
  };

  // Form hook
  const {
    register,
    trigger,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<ContactTemplateData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as Resolver<ContactTemplateData>,
    defaultValues: {
      phone_number:
        (contacts?.phone_numbers &&
          contacts?.phone_numbers.length &&
          contacts?.phone_numbers[0].value) ||
        '',
      email: getEmail(),
      marketing_phone: marketingAgreements?.phone_consent_agreed?.answer,
      marketing_email: marketingAgreements?.email_consent_agreed?.answer,
    },
  });

  useEffect(() => {
    if (!_.isEmpty(contacts) && contacts?.phone_numbers?.length) {
      const { dialCodeLength } = getDialCodeDetails(contactPhoneNumber);

      // Remove intl code from number
      const phoneNumberWithoutIntlCode = contactPhoneNumber
        .substring(dialCodeLength)
        .replace(/\s+/g, '');

      setValue('phone_number', phoneNumberWithoutIntlCode || '', {
        shouldValidate: phoneNumberWithoutIntlCode === '',
      });
      trigger();
    }
  }, [contactPhoneNumber, contacts, setValue, trigger]);

  // Submit Data
  const submitData = (data: ContactTemplateData) => {
    onSubmit({
      phone_number: `${countryCode}${data.phone_number}`,
      email: data.email,
      marketing_email: data.marketing_email,
      marketing_phone: data.marketing_phone,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitData)}>
      <FormLayout sectionTitle='Kontaktdaten'>
        <InputField
          disabled={!hasCoApplicant}
          label='E-Mail-Adresse'
          {...register('email')}
          error={errors.email?.message}
        />

        <InternationalPhoneInput
          label='Mobiltelefonnummer'
          getCode={(value) => setCountry(value)}
          code={countryCode}
          placeholder='40471040'
          {...register('phone_number')}
          error={errors?.phone_number?.message}
        />

        {!isPostBank && (
          <>
            <div className='input-box'>
              <div className='input-label'></div>
              <p className='input-inner-container small-text dark-grey'>
                Ich willige ein, dass Ayvens mich zwecks Information über
                Produkte (Leasingangebote, allgemeine oder vertragsbezogene
                Werbung) und Dienstleistungen (z.B. Vertragsverlängerungen,
                zusätzliche Dienste), Einladung zu Events/Panels und zur Markt-
                und Meinungsforschung kontaktiert, und zwar per:
              </p>
            </div>

            <div className='input-margin'>
              <div></div>
              <Checkbox
                square
                checkMarkType='check'
                checkboxPosition='top'
                type='checkbox'
                {...register('marketing_email')}
                errorMessage={errors.marketing_email?.message}
              >
                <span className='small-text dark-grey'>
                  (optional) E-Mail (Name, Vorname, E-Mail-Adresse)
                </span>
              </Checkbox>
            </div>
            <div className='input-margin'>
              <div></div>
              <Checkbox
                square
                checkMarkType='check'
                checkboxPosition='top'
                type='checkbox'
                {...register('marketing_phone')}
                errorMessage={errors.marketing_phone?.message}
              >
                <span className='small-text dark-grey'>
                  (optional) Telefon, SMS, Messenger (Name, Vorname,
                  Telefonnummer)
                </span>
              </Checkbox>
            </div>

            <div className='input-box'>
              <div className='input-label'></div>
              <p className='input-inner-container small-text dark-grey'>
                Meine Einwilligung umfasst auch eine Analyse von Öffnungs- und
                Klickraten sowie deren Speicherung und Auswertung in
                Empfängerprofilen zu Zwecken der nutzeroptimierten Gestaltung
                künftiger Mitteilungen. Hinweis: Meine Einwilligung kann ich
                jederzeit mit Wirkung für die Zukunft ganz oder teilweise
                widerrufen. Zum Beispiel per E-Mail an{' '}
                <a
                  href={`mailto:${getPartnerText('terms_email')}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {getPartnerText('terms_email')}
                </a>
              </p>
            </div>
          </>
        )}

        {tabButton ? (
          <>
            {device === EDevice.MOBILE ? (
              <ButtonGroup
                className='back-next medium'
                type='back-next'
                data-testid='disabled-forward-button'
                appearOnFront
                buttonOneProps={{
                  type: 'button',
                  name: 'form-submit',
                  dataTestId: 'next',
                  children: 'nächster Schritt',
                  loading,
                  onClick: handleSubmit(submitData),
                }}
              />
            ) : (
              <div className='add-additional-person'>
                <Button
                  theme='secondary'
                  type='button'
                  className='save-button next button-text save-and-next-tab'
                  role='presentation'
                  loading={loading}
                  onClick={handleSubmit(submitData)}
                >
                  nächster Schritt
                </Button>
              </div>
            )}
          </>
        ) : (
          <ButtonGroup
            type='back-next'
            className='back-next'
            buttonOneProps={{
              type: 'submit',
              name: 'form-submit',
              loading,
              dataTestId: 'start-fetching',
              children: 'weiter',
            }}
          />
        )}
      </FormLayout>
    </form>
  );
};

export default ContactTemplate;
